// /pdf/helpers.js
import autoTable from "jspdf-autotable";

/**
 * Add a row of cards (e.g., SDG Indicators)
 */
export function addCards(doc, items, y, cardWidth = 33, cardHeight = 22, gap = 4) {
  let x = 14;
  items.forEach(({ label, value }) => {
    doc.setDrawColor(200, 220, 250);
    doc.setFillColor(230, 245, 255);
    doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, "F");

    doc.setFont("helvetica");
    doc.setFontSize(10);
    doc.text(String(label || ""), x + cardWidth / 2, y + 8, { align: "center" });

    doc.setFontSize(12);
    doc.text(value, x + cardWidth / 2, y + 18, { align: "center" });

    x += cardWidth + gap;
  });
}

export function addGauges(doc, items, startY) {
  const totalWidth = 190;
  const gap = 6;
  const count = items.length;
  const width = (totalWidth - ((count - 1) * gap)) / count;
  const thickness = width * 0.15;
  const r = (width / 2) - (thickness / 2);

  let x = 14 + (width / 2);
  const y = startY + (width / 2);

  items.forEach(item => {
    let val = parseFloat(item.value);
    if (isNaN(val)) val = 0;
    const max = 100;
    const pct = Math.min(Math.max(val / max, 0), 1);

    let color = "#2196F3";
    const txt = (item.label || "").toLowerCase();
    if (txt.includes("safely")) color = "#2E7D32";
    else if (txt.includes("basic")) color = "#00C853";
    else if (txt.includes("limited")) color = "#FFD600";
    else if (txt.includes("unimproved")) color = "#FF6D00";
    else if (txt.includes("open")) color = "#D50000";

    // 1. Draw Grey Background (One single smooth path)
    drawSmoothPath(doc, x, y, r, Math.PI, 2 * Math.PI, "#E0E0E0", thickness);

    // 2. Draw Color Progress
    if (pct > 0) {
      const endAngle = Math.PI + (Math.PI * pct);
      drawSmoothPath(doc, x, y, r, Math.PI, endAngle, color, thickness);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(color);
    doc.text(`${Math.round(val)}%`, x, y - 5, { align: "center", baseline: "middle" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(doc.splitTextToSize(item.label, width + 5), x, y + 5, { align: "center", baseline: "top" });

    x += width + gap;
  });
}

// NEW HELPER: Uses 'doc.lines' to create one continuous vector path
function drawSmoothPath(doc, cx, cy, r, startAngle, endAngle, color, thickness) {
  const steps = 40; // 40 points is enough for a smooth look
  const stepAngle = (endAngle - startAngle) / steps;
  
  // Calculate Start Point
  const startX = cx + r * Math.cos(startAngle);
  const startY = cy + r * Math.sin(startAngle);

  const pathSegments = [];
  
  let prevX = startX;
  let prevY = startY;

  for (let i = 1; i <= steps; i++) {
    const angle = startAngle + (stepAngle * i);
    const px = cx + r * Math.cos(angle);
    const py = cy + r * Math.sin(angle);
    
    // Calculate the difference (delta) from the last point
    pathSegments.push([px - prevX, py - prevY]);
    
    prevX = px;
    prevY = py;
  }

  doc.setDrawColor(color);
  doc.setLineWidth(thickness);
  doc.setLineCap("butt"); 
  doc.setLineJoin("round"); // Smooths the joints between segments
  
  // Draw all segments as ONE single shape
  doc.lines(pathSegments, startX, startY, [1, 1], "S", false);
}



/**
 * Add a row of metrics with icons (e.g., Sanitation Profile)
 */
export function addMetricRow(doc, metrics, y, spacing = 48) {
  let x = 16;
  metrics.forEach(({ icon, label, value }) => {
    if (icon) {
      try {
        doc.addImage(icon, "PNG", x, y, 6, 6);
      } catch (e) {
        console.warn("Invalid icon skipped:", e);
      }
    }

    // always cast to string fallback
    const safeLabel = label ?? "";
    const safeValue = value != null ? String(value) : "-";

    doc.setFontSize(10);
    doc.text(safeLabel, x + 10, y + 3);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(safeValue, x + 10, y + 8);

    x += spacing;
  });
}


export function addMetricRowSM(doc, metrics, y, spacing = 48) {
  let xStart = 16; // initial x
  let rowCount = 0;
  let x = xStart;

  metrics.forEach(({ icon, label, value }, index) => {
    if (icon) {
      try {
        doc.addImage(icon, "PNG", x, y, 6, 6);
      } catch (e) {
        console.warn("Invalid icon skipped:", e);
      }
    }

    const safeLabel = label ?? "";
    const safeValue = value != null ? String(value) : "-";

    doc.setFontSize(10);
    doc.text(safeLabel, x + 8, y + 3);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(safeValue, x + 10, y + 8);

    x += spacing;
    rowCount++;

    // after 4 items → reset x, move y to next row
    if (rowCount === 4) {
      x = xStart;
      y += 15; // adjust row height (tweak as needed)
      rowCount = 0;
    }
  });

  return y + 20; // return updated y for caller
}


/**
 * Add a table using autoTable
 */
export function addTable(doc, headers, rows, y) {
  autoTable(doc, {
    startY: y,
    head: [headers],
    body: rows,
    theme: "grid",
    headStyles: { halign: "center", fillColor: [41, 128, 186] },
    bodyStyles: { halign: "center" },
  });
  return doc.lastAutoTable.finalY + 15;
}
